/**
 * WorkshopBanner — slim dismissible banner at the top of Home v1 (spec §3.2).
 *
 * Dismissal persists to localStorage only; spec's "open question #1" for PM is
 * whether to promote this to an admin-configurable content source. Until then,
 * copy + CTA are hardcoded to the Visibility Strategy Workshop (8th product).
 */

import { useState, useEffect } from "react";
import { Box, IconButton, Typography, alpha, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink } from "react-router-dom";

const DISMISS_KEY = "cf.home.v1.workshopDismissed";

export default function WorkshopBanner() {
  const theme = useTheme();
  const { t } = useTranslation("home");
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (dismissed) {
      try {
        localStorage.setItem(DISMISS_KEY, "1");
      } catch {
        // ignore — private mode, etc.
      }
    }
  }, [dismissed]);

  if (dismissed) return null;

  return (
    <Box
      role="region"
      aria-label={t("WorkshopBanner.announcementAriaLabel")}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: { xs: 2, md: 3 },
        py: 1.25,
        bgcolor: alpha(theme.palette.secondary.main, 0.08),
        borderBottom: 1,
        borderColor: "divider",
        flexWrap: { xs: "wrap", md: "nowrap" },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          fontSize: 13.5,
          color: "text.secondary",
          minWidth: 0,
        }}
      >
        <Box
          component="span"
          sx={{
            fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "secondary.main",
          }}
        >
          {t("WorkshopBanner.freeWorkshopBadge")}
        </Box>
        <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
          {t("WorkshopBanner.bodyText")}
        </Box>
        <Box
          component={RouterLink}
          to="/visibility-strategy"
          sx={{
            color: "secondary.main",
            fontWeight: 500,
            textDecoration: "none",
            whiteSpace: "nowrap",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {t("WorkshopBanner.startWorkshopCta")}
        </Box>
      </Typography>
      <IconButton
        aria-label={t("WorkshopBanner.dismissAriaLabel")}
        onClick={() => setDismissed(true)}
        size="small"
        sx={{ flexShrink: 0, color: "text.tertiary" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
